import React, { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { useEditAgentMutation, useDownloadAgentMdQuery } from '../../data/api/endPointsSlice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  CircularProgress,
  Backdrop,
  Typography,
} from '@mui/material';
import { AgentMarketPlaceItemWithFilter } from '../../pages/userAgent/agentMarketPlace/AgentMarketPlace';

interface AgentEditModalProps {
  open: boolean;
  editData: Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string }>;
  onChange: (field: keyof AgentMarketPlaceItemWithFilter | 'readme_md', value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  onDownload: (agentData: Partial<AgentMarketPlaceItemWithFilter & { folder_name?: string }>) => void;
}


const AgentEditModal: React.FC<AgentEditModalProps> = ({ open, editData, onChange, onSave, onCancel, onDownload }) => {
  const [editAgent, { isLoading: isEditingAgent }] = useEditAgentMutation();
  const [readmeValue, setReadmeValue] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [readmeEdited, setReadmeEdited] = useState(false);

  // Fetch README.md when modal opens and agent id is present
  const partyId = editData?._id;
  const { data: readmeMd, isFetching: isReadmeLoading } = useDownloadAgentMdQuery(partyId ?? '', { skip: !open || !partyId });

  useEffect(() => {
    if (open && partyId && readmeMd !== undefined && !isInitialized) {
      setReadmeValue(readmeMd);
      setReadmeEdited(false);
      setIsInitialized(true);
    } else if (!open) {
      setIsInitialized(false);
      setReadmeValue('');
      setReadmeEdited(false);
    }
  }, [open, partyId, readmeMd, isInitialized]);

  // Helper to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Wrap onChange to auto-update last_updated only if a different field is changed
  const handleFieldChange = (field: keyof AgentMarketPlaceItemWithFilter | 'readme_md', value: any) => {
    onChange(field, value);
    if (field !== 'last_updated') {
      onChange('last_updated', getTodayDate());
    }
  };

  const handleReadmeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setReadmeValue(e.target.value);
    setReadmeEdited(true);
    handleFieldChange('readme_md', e.target.value);
  };

  const handleSave = async () => {
    if (!editData || !editData._id || !editData.folder_name) {
      if (onSave) onSave();
      return;
    }

    // Only include user-configurable fields in config.json, exclude system/metadata fields
    // Order matches the original config.json structure from agent creation
    const userConfigurableFields = [
      '_id', 'name', 'folder_name', 'party_type', 'description', 'service_name', 'port', 'allow_all_access',
      'owners', 'last_updated', 'icon', 'category', 'role', 'isPlatformAgent', 'datasetIds', 'supports_file_upload',
      'file_format_supported', 'prompt1', 'prompt2', 'prompt3', 'tech_stack', 'cloud_provider', 'custom_ui',
      'supports_dataset', 'ui_url', 'demo_video_url', 'downloadable_only'
    ];

    const config: any = {};
    userConfigurableFields.forEach(field => {
      if ((editData as any)[field] !== undefined) {
        const configKey = field === '_id' ? 'party_id' : field;
        config[configKey] = (editData as any)[field];
      }
    });

    // Only include README.md if the user actually edited it
    const zip = new JSZip();
    const folder = zip.folder(editData.folder_name);

    // Always include config.json
    (folder ?? zip).file('config.json', JSON.stringify(config, null, 2));

    // Include README.md only if user edited it and it's not empty
    if (readmeEdited && readmeValue !== undefined && readmeValue !== '') {
      (folder ?? zip).file('README.md', readmeValue);
    }

    // If only config is edited, only config.json will be in the zip
    // If README.md is changed, it will be included

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const formData = new FormData();
    formData.append('agent_file', new File([zipBlob], `${editData.folder_name}.zip`, { type: 'application/zip' }));
    await editAgent({ partyId: editData._id, agentData: formData });
    if (onSave) onSave();
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Edit Agent</DialogTitle>
      <DialogContent dividers>
        {/* Edit Agent Loader Overlay */}
        {isEditingAgent && (
          <Backdrop
            sx={{
              position: 'absolute',
              zIndex: (theme) => theme.zIndex.drawer + 1,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: 1,
            }}
            open={isEditingAgent}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                Saving Agent...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we save your changes
              </Typography>
            </Box>
          </Backdrop>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* ...existing code for fields... */}
          <TextField label="Party ID" value={editData._id || ''} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Name" value={editData.name === null ? 'null' : (editData.name || '')} onChange={e => handleFieldChange('name', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Party Type" value={editData.party_type === null ? 'null' : (editData.party_type || '')} onChange={e => handleFieldChange('party_type', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Description" value={editData.description === null ? 'null' : (editData.description || '')} onChange={e => handleFieldChange('description', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Tech Stack (comma separated)" value={editData.tech_stack === null ? 'null' : (editData.tech_stack ? editData.tech_stack.join(', ') : '')} onChange={e => handleFieldChange('tech_stack', e.target.value === 'null' ? null : e.target.value.split(',').map((t) => t.trim()))} fullWidth sx={{ mb: 2 }} />
          <TextField label="Category" value={editData.category === null ? 'null' : (editData.category || '')} onChange={e => handleFieldChange('category', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Role" value={editData.role === null ? 'null' : (editData.role || '')} onChange={e => handleFieldChange('role', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Cloud Provider" value={editData.cloud_provider === null ? 'null' : (editData.cloud_provider || '')} onChange={e => handleFieldChange('cloud_provider', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Allow All Access" value={editData.allow_all_access === null ? 'null' : (editData.allow_all_access ? 'true' : 'false')} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Owners (name:email, ...)" value={editData.owners === null ? 'null' : (editData.owners ? editData.owners.map((o) => `${o.name}:${o.email}`).join(', ') : '')} onChange={e => handleFieldChange('owners', e.target.value === 'null' ? null : e.target.value.split(',').map((str) => { const [name, email] = str.split(':'); return { name: name?.trim() || '', email: email?.trim() || '' }; }))} fullWidth sx={{ mb: 2 }} />
          <TextField 
            label="Last Updated" 
            value={editData.last_updated === null ? 'null' : (editData.last_updated || '')}
            onChange={e => handleFieldChange('last_updated', e.target.value === 'null' ? null : e.target.value)}
            fullWidth 
            sx={{ mb: 2 }} 
          />
          <TextField label="Folder Name" value={editData.folder_name === null ? 'null' : (editData.folder_name || '')} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Icon" value={editData.icon === null ? 'null' : (editData.icon || '')} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Is Platform Agent" value={editData.isPlatformAgent === null ? 'null' : (editData.isPlatformAgent ? 'true' : 'false')} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Downloadable Only" value={editData.downloadable_only === null ? 'null' : (editData.downloadable_only ? 'true' : 'false')} onChange={e => handleFieldChange('downloadable_only', e.target.value === 'null' ? null : e.target.value === 'true')} fullWidth sx={{ mb: 2 }} />
          <TextField label="Service Name" value={editData.service_name === null ? 'null' : (editData.service_name || '')} fullWidth sx={{ mb: 2 }} disabled />
          <TextField label="Port" type="number" value={editData.port === null ? 'null' : (editData.port ?? '')} onChange={e => handleFieldChange('port', e.target.value === 'null' ? null : Number(e.target.value))} fullWidth sx={{ mb: 2 }} />
          <TextField label="Supports File Upload" value={editData.supports_file_upload === null ? 'null' : (editData.supports_file_upload ? 'true' : 'false')} onChange={e => handleFieldChange('supports_file_upload', e.target.value === 'null' ? null : e.target.value === 'true')} fullWidth sx={{ mb: 2 }} />
          <TextField label="File Format Supported (comma separated)" value={editData.file_format_supported === null ? 'null' : (editData.file_format_supported ? (Array.isArray(editData.file_format_supported) ? editData.file_format_supported.join(', ') : editData.file_format_supported) : '')} onChange={e => handleFieldChange('file_format_supported', e.target.value === 'null' ? null : e.target.value.split(',').map((f) => f.trim()))} fullWidth sx={{ mb: 2 }} />
          <TextField label="Supports Dataset" value={editData.supports_dataset === null ? 'null' : (editData.supports_dataset || '')} onChange={e => handleFieldChange('supports_dataset', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Dataset ID" value={editData.datasetIds === null ? 'null' : (editData.datasetIds || '')} onChange={e => handleFieldChange('datasetIds', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Custom UI" value={editData.custom_ui === null ? 'null' : (editData.custom_ui || '')} onChange={e => handleFieldChange('custom_ui', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="UI URL" value={editData.ui_url === null ? 'null' : (editData.ui_url || '')} onChange={e => handleFieldChange('ui_url', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />

          {/* Download URL field: show 'null' if empty, else show the URL */}
          <TextField
            label="Download URL"
            value={editData.download_url === null || editData.download_url === undefined || editData.download_url === '' ? 'null' : editData.download_url}
            onChange={e => handleFieldChange('download_url', e.target.value === 'null' ? null : e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField label="Demo Video URL" value={editData.demo_video_url === null ? 'null' : (editData.demo_video_url || '')} onChange={e => handleFieldChange('demo_video_url', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Prompt 1" value={editData.prompt1 === null ? 'null' : (editData.prompt1 || '')} onChange={e => handleFieldChange('prompt1', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Prompt 2" value={editData.prompt2 === null ? 'null' : (editData.prompt2 || '')} onChange={e => handleFieldChange('prompt2', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Prompt 3" value={editData.prompt3 === null ? 'null' : (editData.prompt3 || '')} onChange={e => handleFieldChange('prompt3', e.target.value === 'null' ? null : e.target.value)} fullWidth sx={{ mb: 2 }} />
          {/* Editable README.md field */}
          <TextField
            label="README.md"
            value={readmeValue}
            onChange={handleReadmeChange}
            fullWidth
            multiline
            minRows={6}
            sx={{ mb: 2 }}
            placeholder={isReadmeLoading ? 'Loading README.md...' : 'Edit README.md content here'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isEditingAgent}
        >
          Save
        </Button>
        <Button onClick={onCancel} variant="outlined" disabled={isEditingAgent}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
export default AgentEditModal;